import { ExtractionConfirmFormEnglishLevelEnum } from 'modules/jobOrderApply/types/ExtractionConfirmFormEnglishLevel.enum';
import { JobOrderApplicationFlowEnum } from 'modules/jobOrderApply/types/JobOrderApplicationFlow.enum';
import { mapSkills } from 'modules/jobOrderApply/utils/mapSkills';
import { JobOrderApplicationFragmentFragment } from 'shared/hooks/api/fragments/jobOrderApplication.fragment.generated';
import { safeDateParse } from 'shared/utils/safeDateParse';
import { AvailabilityEnum, mapDtoToAvailabilityEnum } from 'types/enums/availability-enum';
import {
  JobOrderApplicationProcessStatusEnum,
  mapDtoToEnum,
} from 'types/enums/job-order-application-process-status.enum';
import { mapDtoToWorkTypeEnum, WorkTypeEnum } from 'types/enums/work-type.enum';
import { Skill } from 'types/Skill';

export interface JobOrderApplication {
  applicationId: string;
  jobOrderId: string;
  externalOfferId: string | null;
  createdAt: Date;
  meetingDates: Date[];
  selectedMeetingDate: Date | null;
  ciDate: Date | null;
  cqDate: Date | null;
  processStatus: JobOrderApplicationProcessStatusEnum;
  prevProcessStatus: JobOrderApplicationProcessStatusEnum | null;
  meetingUrl: string | null;
  isViewedByExpert: boolean;
  handlerId: string | null;
  preppingId: string | null;
  flow: JobOrderApplicationFlowEnum | null;
  contactByPhone: boolean | null;
  isPreferencesFinished: boolean;
  hasActiveAirInterviewInvitation: boolean;
  preferences: {
    location: string | null;
    workType: WorkTypeEnum | null;
    rateDesired: number | null;
    availability: AvailabilityEnum | null;
    experienceYears: number | null;
    englishLevel: ExtractionConfirmFormEnglishLevelEnum | null;
    skillsMustHave: Skill[] | null;
    skillsNiceToHave: Skill[] | null;
  } | null;
}

export const mapDtoToJobOrderApplicationPreferences = (
  dto: JobOrderApplicationFragmentFragment['preferences'],
): JobOrderApplication['preferences'] =>
  dto
    ? {
        location: dto.location || null,
        workType: dto.workType ? mapDtoToWorkTypeEnum(dto.workType) : null,
        rateDesired: dto.rateDesired ?? null,
        availability: dto.availability
          ? mapDtoToAvailabilityEnum(dto.availability || null, dto.noticeLength || null)
          : null,
        experienceYears: dto.experienceYears ?? null,
        englishLevel:
          (dto.englishLevel as unknown as ExtractionConfirmFormEnglishLevelEnum) ?? null,
        skillsMustHave: dto.skillsMustHave ? mapSkills(dto.skillsMustHave) : null,
        skillsNiceToHave: dto.skillsNiceToHave ? mapSkills(dto.skillsNiceToHave) : null,
      }
    : null;

export const mapDtoToJobOrderApplication = (
  dto: JobOrderApplicationFragmentFragment,
): JobOrderApplication => ({
  applicationId: dto.applicationId,
  jobOrderId: dto.jobOrderId,
  externalOfferId: dto.publicOfferId || null,
  createdAt: new Date(dto.createdAt),
  meetingDates: dto.meetingDates ? dto.meetingDates.map((stringDate) => new Date(stringDate)) : [],
  selectedMeetingDate:
    (dto.processStatus as unknown as JobOrderApplicationProcessStatusEnum) ===
    JobOrderApplicationProcessStatusEnum.ScheduledForCQ
      ? safeDateParse(dto.cqDate)
      : safeDateParse(dto.ciDate),
  ciDate: safeDateParse(dto.ciDate),
  cqDate: safeDateParse(dto.cqDate),
  processStatus: mapDtoToEnum(dto.processStatus),
  prevProcessStatus: dto.prevProcessStatus ? mapDtoToEnum(dto.prevProcessStatus) : null,
  meetingUrl: dto.meetingUrl || null,
  isViewedByExpert: dto.isViewedByExpert,
  handlerId: dto.handlerId || null,
  preppingId: dto.preppingId || null,
  flow: dto.flow || null,
  contactByPhone: dto.contactByPhone || null,
  isPreferencesFinished: dto.isPreferencesFinished,
  hasActiveAirInterviewInvitation: dto.hasActiveAirInterviewInvitation,
  preferences: mapDtoToJobOrderApplicationPreferences(dto.preferences),
});
